import React from 'react'
import { useI18n } from '../../i18n'
import { featureFlags } from '../../config/featureFlags'
import { GovernanceListingSection } from '../governance/GovernanceListingSection'
import { fetchSystemExecutiveContribution } from '../../services/executiveGovernanceApi'

type Props = {
  informationSystemId: string
}

export default function InformationSystemExecutiveGovernancePanel({ informationSystemId }: Props) {
  const { t } = useI18n()
  const [data, setData] = React.useState<{
    executiveCriticality?: string
    governancePressure?: number
    blastRadiusContribution?: Array<{ controlCode: string; summary: string }>
    certificationInstabilityContribution?: number
  } | null>(null)

  React.useEffect(() => {
    if (!featureFlags.EXECUTIVE_GOVERNANCE_V1 || !informationSystemId) return
    void fetchSystemExecutiveContribution(informationSystemId).then(setData).catch(() => setData(null))
  }, [informationSystemId])

  if (!featureFlags.EXECUTIVE_GOVERNANCE_V1) return null

  return (
    <GovernanceListingSection
      title={t('executiveGovernance.executiveOversight', 'Executive Oversight')}
      description={t(
        'executiveGovernance.isPanelHint',
        'Executive criticality, governance pressure, and certification instability contribution for this system.',
      )}
    >
      {!data ? (
        <p className="text-xs text-gray-500 m-0 italic">{t('common.loading', 'Loading…')}</p>
      ) : (
        <ul className="text-xs text-slate-700 m-0 p-0 list-none space-y-1">
          <li>
            {t('executiveGovernance.executiveCriticality', 'Executive criticality')}: {data.executiveCriticality || '—'}
          </li>
          <li>
            {t('executiveGovernance.operationalPressure', 'Operational Pressure')}: {data.governancePressure ?? 0}
          </li>
          <li>
            {t('executiveGovernance.certificationRisk', 'Certification Risk')}:{' '}
            {data.certificationInstabilityContribution ?? 0}{' '}
            {t('executiveGovernance.degradedControls', 'degraded controls')}
          </li>
          {(data.blastRadiusContribution || []).slice(0, 3).map((b) => (
            <li key={b.controlCode} className="text-gray-600">
              {b.summary}
            </li>
          ))}
        </ul>
      )}
    </GovernanceListingSection>
  )
}
